import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import {
  mapDisplayCodeToClassification,
  resolveEtaDayRange,
} from '@theme/delivery-estimate';

const STORAGE_KEY = 'beaumont-selected-state';
const STATE_CHANGE_EVENT = 'state-selector:change';
export const PURCHASE_MODE_CHANGE_EVENT = 'beaumont:purchase-mode-change';
const CONTACT_CODES = new Set(['C', 'D']);
const SAMPLE_CODE = 'S';

/** Indent / special-order display category — shows PDP special-order notice. */
const SPECIAL_ORDER_CODE = 'B';

/** Display codes that remain purchasable at zero inventory (backorder per FSD / WIP). */
export const BACKORDER_DISPLAY_CODES = new Set(['R', 'A', 'B', 'H', 'S', 'Y']);

/**
 * @param {string | undefined} displayCode
 * @param {number} inventoryQuantity
 * @returns {boolean}
 */
export function isBackorderEligible(displayCode, inventoryQuantity) {
  if (!displayCode || !BACKORDER_DISPLAY_CODES.has(displayCode)) {
    return false;
  }

  return Number.isFinite(inventoryQuantity) && inventoryQuantity <= 0;
}

/**
 * @param {HTMLElement} element
 * @returns {import('@theme/delivery-estimate').EtaWindowsMap}
 */
function getEtaWindowsFromElement(element) {
  const embedded = element.querySelector('script[type="application/json"][data-eta-windows]');

  if (!(embedded instanceof HTMLScriptElement)) return {};

  try {
    const parsed = JSON.parse(embedded.textContent || '{}');
    return /** @type {import('@theme/delivery-estimate').EtaWindowsMap} */ (
      parsed.etaWindows ?? {}
    );
  } catch {
    return {};
  }
}

/**
 * Product merchandising leads, but a missing shop `shipping.eta_windows` path
 * is treated as Z / unavailable (aligned with stock-status label fallback).
 *
 * @param {{
 *   selectedState: string | null,
 *   classifications: Record<string, string>,
 *   availableStates: string[],
 *   etaWindows?: import('@theme/delivery-estimate').EtaWindowsMap | null,
 *   inventoryQuantity?: number | null,
 * }} params
 * @returns {{ view: 'purchase' | 'contact-cs' | 'no-state' | 'unavailable', code?: string, state?: string }}
 */
export function resolveAvailability({
  selectedState,
  classifications,
  availableStates,
  etaWindows = null,
  inventoryQuantity = null,
}) {
  const hasClassifications = classifications && Object.keys(classifications).length > 0;
  const hasAvailableStates = Array.isArray(availableStates) && availableStates.length > 0;

  if (!selectedState) {
    return { view: 'no-state' };
  }

  // Empty merchandising data defaults to Z (unavailable) — do not allow purchase.
  if (!hasClassifications && !hasAvailableStates) {
    return { view: 'unavailable', state: selectedState, code: 'Z' };
  }

  if (hasAvailableStates && !availableStates.includes(selectedState)) {
    return { view: 'unavailable', state: selectedState };
  }

  const code = classifications?.[selectedState];

  if (!code || code === 'Z') {
    return { view: 'unavailable', state: selectedState, code: code || 'Z' };
  }

  if (CONTACT_CODES.has(code)) {
    return { view: 'contact-cs', code };
  }

  // Sample uses flat messaging — does not require an eta_windows day range.
  if (code === SAMPLE_CODE) {
    return { view: 'purchase', code };
  }

  const classification = mapDisplayCodeToClassification(code, inventoryQuantity);
  const dayRange = classification
    ? resolveEtaDayRange(etaWindows, selectedState, classification)
    : null;

  if (!dayRange) {
    return { view: 'unavailable', state: selectedState, code: 'Z' };
  }

  return { view: 'purchase', code };
}

/**
 * @param {string} attribute
 * @param {HTMLElement} element
 * @returns {unknown}
 */
function parseJsonAttribute(attribute, element) {
  const raw = element.getAttribute(attribute);

  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * @typedef {object} Refs
 * @property {HTMLElement} purchaseView
 * @property {HTMLElement} contactCsView
 *
 * @extends {Component<Refs>}
 */
class ProductAvailabilityComponent extends Component {
  requiredRefs = ['purchaseView', 'contactCsView'];

  connectedCallback() {
    super.connectedCallback();
    this.#updateView();
    document.addEventListener(STATE_CHANGE_EVENT, this.#handleStateChange);
    window.addEventListener('storage', this.#handleStorageChange);
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(STATE_CHANGE_EVENT, this.#handleStateChange);
    window.removeEventListener('storage', this.#handleStorageChange);
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  #applyDataFromElement(source) {
    const attributes = [
      'data-state-classifications',
      'data-available-states',
      'data-state-names',
      'data-inventory-quantity',
      'data-add-to-cart-label',
      'data-delivery-only',
    ];

    for (const attribute of attributes) {
      const value = source.getAttribute(attribute);

      if (value !== null) {
        this.setAttribute(attribute, value);
      }
    }

    const newWindowsScript = source.querySelector('script[type="application/json"][data-eta-windows]');
    const currentWindowsScript = this.querySelector('script[type="application/json"][data-eta-windows]');

    if (newWindowsScript instanceof HTMLScriptElement) {
      if (currentWindowsScript instanceof HTMLScriptElement) {
        currentWindowsScript.textContent = newWindowsScript.textContent;
      } else {
        this.prepend(newWindowsScript.cloneNode(true));
      }
    }
  }

  #isDeliveryOnly() {
    return this.dataset.deliveryOnly === 'true';
  }

  /**
   * Delivery-only is an info notice on the purchase path — does not switch to Contact CS.
   * @param {{ view: string }} result
   */
  #updateDeliveryOnlyNotice(result) {
    const notice =
      this.refs.deliveryOnlyNotice ?? this.querySelector('[data-delivery-only-notice]');

    if (!(notice instanceof HTMLElement)) return;

    const show = result.view === 'purchase' && this.#isDeliveryOnly();
    notice.hidden = !show;
  }

  /**
   * Special order (display category B / indent) — info notice + PDP badge.
   * Driven by merchandising.state_classifications for the selected state.
   * @param {{ view: string, code?: string }} result
   */
  #updateSpecialOrderNotice(result) {
    const show = result.view === 'purchase' && result.code === SPECIAL_ORDER_CODE;

    const notice =
      this.refs.specialOrderNotice ?? this.querySelector('[data-special-order-notice]');

    if (notice instanceof HTMLElement) {
      notice.hidden = !show;
    }

    document.querySelectorAll('[data-special-order-badge]').forEach((badge) => {
      if (badge instanceof HTMLElement) {
        badge.hidden = !show;
      }
    });
  }

  /**
   * Re-sync metafield data after variant / combined-listing product switches.
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const { newProduct, html } = event.detail.data;

    if (newProduct) {
      this.dataset.productId = newProduct.id;

      const newComponent = html.querySelector(
        `product-availability-component[data-product-id="${newProduct.id}"]`
      );

      if (newComponent instanceof HTMLElement) {
        this.#applyDataFromElement(newComponent);
      }

      this.#syncInventoryFromResponse(html);
      this.updatedCallback();
      queueMicrotask(() => {
        this.#updateView();
      });
      return;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newComponent = html.querySelector(
      `product-availability-component[data-product-id="${this.dataset.productId}"]`
    );

    if (!newComponent) return;

    if (newComponent instanceof HTMLElement) {
      this.#applyDataFromElement(newComponent);
    }

    this.#syncInventoryFromResponse(html);

    morph(this, newComponent, { childrenOnly: true });
    this.updatedCallback();
    queueMicrotask(() => {
      this.#updateView();
    });
  };

  /**
   * @param {Document} html
   */
  #syncInventoryFromResponse(html) {
    const stockStatus = html.querySelector(`product-stock-status[data-product-id="${this.dataset.productId}"]`);

    if (stockStatus instanceof HTMLElement && stockStatus.dataset.inventoryQuantity !== undefined) {
      this.dataset.inventoryQuantity = stockStatus.dataset.inventoryQuantity;
    }
  }

  #handleStateChange = () => {
    this.#updateView();
  };

  /**
   * @param {StorageEvent} event
   */
  #handleStorageChange = (event) => {
    if (event.key !== STORAGE_KEY) return;

    this.#updateView();
  };

  #updateView() {
    const selectedState = sessionStorage.getItem(STORAGE_KEY);
    const classifications = parseJsonAttribute('data-state-classifications', this) ?? {};
    const availableStates = parseJsonAttribute('data-available-states', this) ?? [];
    const inventoryQuantity = Number(this.dataset.inventoryQuantity);
    const etaWindows = getEtaWindowsFromElement(this);

    const result = resolveAvailability({
      selectedState,
      classifications: /** @type {Record<string, string>} */ (classifications),
      availableStates: /** @type {string[]} */ (availableStates),
      etaWindows,
      inventoryQuantity: Number.isFinite(inventoryQuantity) ? inventoryQuantity : null,
    });

    this.refs.purchaseView.hidden = result.view !== 'purchase';
    this.refs.contactCsView.hidden = result.view !== 'contact-cs';

    this.dataset.availabilityView = result.view;
    this.#updateDeliveryOnlyNotice(result);
    this.#updateSpecialOrderNotice(result);
    this.#updatePurchaseControls(result);
    this.dispatchEvent(new CustomEvent('beaumont:availability-updated', { bubbles: true }));
  }

  /**
   * @param {{ view: string, code?: string }} result
   */
  #updatePurchaseControls(result) {
    const inventoryQuantity = Number(this.dataset.inventoryQuantity);
    const backorder =
      result.view === 'purchase' &&
      Boolean(result.code) &&
      isBackorderEligible(result.code, inventoryQuantity);

    this.dataset.purchaseMode = backorder ? 'backorder' : result.view === 'purchase' ? 'standard' : '';

    if (result.view !== 'purchase') {
      this.#dispatchPurchaseModeChange(false);
      return;
    }

    if (!backorder) {
      this.#dispatchPurchaseModeChange(false);
      return;
    }

    const addToCartLabel = this.dataset.addToCartLabel || 'Add to cart';

    this.refs.purchaseView.querySelectorAll('[ref="addToCartButton"]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      button.disabled = false;

      const textSpan = button.querySelector('.add-to-cart-text__content span span');

      if (textSpan) {
        textSpan.textContent = addToCartLabel;
      }
    });

    this.#dispatchPurchaseModeChange(true);
  }

  /**
   * @param {boolean} backorder
   */
  #dispatchPurchaseModeChange(backorder) {
    this.dispatchEvent(
      new CustomEvent(PURCHASE_MODE_CHANGE_EVENT, {
        bubbles: true,
        detail: { backorder },
      })
    );
  }
}

if (!customElements.get('product-availability-component')) {
  customElements.define('product-availability-component', ProductAvailabilityComponent);
}
